import type { TenantAccountListItem, TenantItem } from '../../../apis/types'

export type TenantListRowKind = 'tenant' | 'account'

export interface TenantListRow {
  /** Unique row id for Table keys (tenant id or account id). */
  id: string
  kind: TenantListRowKind
  tenant: TenantItem
  account?: TenantAccountListItem
}

export const compareKingAccountNumber = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

export const sortByKingAccountNumber = <T extends { kingAccountNumber: string }>(accounts: T[]): T[] =>
  [...accounts].sort((a, b) => compareKingAccountNumber(a.kingAccountNumber, b.kingAccountNumber))

/** Flatten tenants into tenant + optional nested account rows based on expanded set. */
export const flattenTenantListRows = (
  tenants: TenantItem[],
  expandedIds: ReadonlySet<string>
): TenantListRow[] => {
  const rows: TenantListRow[] = []
  for (const tenant of tenants) {
    rows.push({ id: tenant.id, kind: 'tenant', tenant })
    if (!expandedIds.has(tenant.id)) continue
    const accounts = sortByKingAccountNumber(tenant.serviceAccounts ?? [])
    for (const account of accounts) {
      rows.push({
        id: account.id,
        kind: 'account',
        tenant,
        account,
      })
    }
  }
  return rows
}

export const tenantListRowHref = (row: TenantListRow): string => {
  if (row.kind === 'account' && row.account != null) {
    return `/tenant/tenants/${row.tenant.id}?serviceAccountNumber=${encodeURIComponent(row.account.kingAccountNumber)}`
  }
  return `/tenant/tenants/${row.tenant.id}`
}

export const allTenantIds = (tenants: TenantItem[]): string[] => tenants.map((t) => t.id)

export const areAllTenantsExpanded = (
  tenants: TenantItem[],
  expandedIds: ReadonlySet<string>
): boolean => tenants.length > 0 && tenants.every((t) => expandedIds.has(t.id))
